import { useMemo, useState, type ReactNode } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTriggerWidth } from "@/components/ui/use-trigger-width";
import { cn } from "@/lib/utils";
import { AccountSelect } from "./account-select";
import { AccountMultiSelect } from "./account-multi-select";
import { GroupSelect } from "./group-select";
import { AccountMenu, type AccountMenuProps } from "./account-menu";
import {
  AccountMultiSelectMenu,
  type AccountMultiSelectMenuProps,
} from "./account-multi-select-menu";
import { GroupMenu, type GroupMenuProps } from "./group-menu";
import { SelectorField } from "./selector-trigger";
import type {
  AccountMenuChoice,
  AccountSelectSelection,
  SelectorAccount,
  SelectorFieldProps,
  SelectorGroup,
  SelectorSize,
} from "./types";

interface CommonDropdownProps extends SelectorFieldProps {
  id?: string;
  size?: SelectorSize;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  "aria-label"?: string;
  "data-testid"?: string;
}

function resolveAccountSelection(
  choice: AccountMenuChoice | undefined,
  accounts: SelectorAccount[],
  groups: SelectorGroup[],
  allLabel?: string
): AccountSelectSelection | undefined {
  if (!choice) return undefined;
  if (choice.kind === "all") return { kind: "all", label: allLabel };
  if (choice.kind === "group") {
    const group = groups.find((item) => item.id === choice.id);
    return group ? { kind: "group", group } : undefined;
  }
  const account = accounts.find((item) => item.id === choice.id);
  return account ? { kind: "account", account } : undefined;
}

export interface AccountDropdownProps
  extends
    CommonDropdownProps,
    Pick<
      AccountMenuProps,
      | "accounts"
      | "groups"
      | "searchable"
      | "includeAll"
      | "selectableGroups"
      | "allLabel"
      | "searchPlaceholder"
      | "emptyMessage"
    > {
  value?: AccountMenuChoice;
  onChange: (choice: AccountMenuChoice) => void;
  onClear?: () => void;
  menuTestId?: string;
}

export function AccountDropdown({
  accounts,
  groups = [],
  value,
  onChange,
  size,
  placeholder,
  disabled,
  clearable,
  onClear,
  label,
  hint,
  error,
  required,
  className,
  triggerClassName,
  menuClassName,
  searchable,
  includeAll,
  selectableGroups,
  allLabel,
  searchPlaceholder,
  emptyMessage,
  id,
  "aria-label": ariaLabel,
  "data-testid": testId = "accountPickerSelectContainer",
  menuTestId = "accountPickerSelectMenu",
}: AccountDropdownProps) {
  const [open, setOpen] = useState(false);
  const { triggerRef, triggerWidthStyle } =
    useTriggerWidth<HTMLButtonElement>(open);
  const selection = resolveAccountSelection(value, accounts, groups, allLabel);

  return (
    <SelectorField
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={className}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <AccountSelect
            ref={triggerRef}
            selection={selection}
            id={id}
            size={size}
            open={open}
            placeholder={placeholder}
            disabled={disabled}
            error={Boolean(error)}
            clearable={clearable && Boolean(value)}
            onClear={onClear}
            className={triggerClassName}
            aria-label={ariaLabel}
            data-testid={testId}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          disableAnimation
          widthMode="trigger"
          style={triggerWidthStyle}
          data-testid={menuTestId}
          className={cn("rounded-lg border-0 p-0 shadow-none", menuClassName)}
        >
          <AccountMenu
            accounts={accounts}
            groups={groups}
            value={value}
            size={size}
            searchable={searchable}
            includeAll={includeAll}
            selectableGroups={selectableGroups}
            allLabel={allLabel}
            searchPlaceholder={searchPlaceholder}
            emptyMessage={emptyMessage}
            onChange={(choice) => {
              onChange(choice);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </SelectorField>
  );
}

export interface AccountMultiSelectDropdownProps
  extends
    CommonDropdownProps,
    Pick<
      AccountMultiSelectMenuProps,
      | "accounts"
      | "groups"
      | "selectedIds"
      | "searchable"
      | "searchPlaceholder"
      | "emptyMessage"
      | "selectAllLabel"
      | "ungroupedLabel"
      | "showSelectAll"
      | "listClassName"
    > {
  onChange: (selectedIds: string[]) => void;
  onClear?: () => void;
  closeOnSelect?: boolean;
  selectedGroup?: SelectorGroup;
  summary?: ReactNode;
  maxVisibleAccounts?: number;
  "data-testid"?: string;
  menuTestId?: string;
}

export function AccountMultiSelectDropdown({
  accounts,
  groups = [],
  selectedIds,
  onChange,
  size,
  placeholder,
  disabled,
  clearable,
  onClear,
  closeOnSelect = false,
  selectedGroup,
  summary,
  maxVisibleAccounts,
  label,
  hint,
  error,
  required,
  className,
  triggerClassName,
  menuClassName,
  searchable,
  searchPlaceholder,
  emptyMessage,
  selectAllLabel,
  ungroupedLabel,
  showSelectAll,
  listClassName,
  id,
  "aria-label": ariaLabel,
  "data-testid": testId = "accountPickerSelectContainer",
  menuTestId = "accountPickerSelectMenu",
}: AccountMultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const { triggerRef, triggerWidthStyle } =
    useTriggerWidth<HTMLButtonElement>(open);

  return (
    <SelectorField
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={className}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <AccountMultiSelect
            ref={triggerRef}
            accounts={accounts}
            id={id}
            groups={groups}
            selectedIds={selectedIds}
            selectedGroup={selectedGroup}
            summary={summary}
            maxVisibleAccounts={maxVisibleAccounts}
            size={size}
            open={open}
            placeholder={placeholder}
            disabled={disabled}
            error={Boolean(error)}
            clearable={clearable && selectedIds.length > 0}
            onClear={onClear || (() => onChange([]))}
            className={triggerClassName}
            aria-label={ariaLabel}
            data-testid={testId}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          disableAnimation
          widthMode="trigger"
          style={triggerWidthStyle}
          data-testid={menuTestId}
          className={cn("rounded-lg border-0 p-0 shadow-none", menuClassName)}
        >
          <AccountMultiSelectMenu
            accounts={accounts}
            groups={groups}
            selectedIds={selectedIds}
            size={size}
            searchable={searchable}
            searchPlaceholder={searchPlaceholder}
            emptyMessage={emptyMessage}
            selectAllLabel={selectAllLabel}
            ungroupedLabel={ungroupedLabel}
            showSelectAll={showSelectAll}
            listClassName={listClassName}
            onChange={(ids) => {
              onChange(ids);
              if (closeOnSelect) setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </SelectorField>
  );
}

export interface GroupDropdownProps
  extends
    CommonDropdownProps,
    Pick<
      GroupMenuProps,
      "groups" | "searchable" | "searchPlaceholder" | "emptyMessage"
    > {
  value?: string;
  onChange: (groupId: string) => void;
  onClear?: () => void;
}

export function GroupDropdown({
  groups,
  value,
  onChange,
  size,
  placeholder,
  disabled,
  clearable,
  onClear,
  label,
  hint,
  error,
  required,
  className,
  triggerClassName,
  menuClassName,
  searchable,
  searchPlaceholder,
  emptyMessage,
  id,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: GroupDropdownProps) {
  const [open, setOpen] = useState(false);
  const { triggerRef, triggerWidthStyle } =
    useTriggerWidth<HTMLButtonElement>(open);
  const selectedGroup = useMemo(
    () => groups.find((group) => group.id === value),
    [groups, value]
  );

  return (
    <SelectorField
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={className}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <GroupSelect
            ref={triggerRef}
            value={selectedGroup}
            id={id}
            size={size}
            open={open}
            placeholder={placeholder}
            disabled={disabled || groups.length === 0}
            error={Boolean(error)}
            clearable={clearable && Boolean(value)}
            onClear={onClear}
            className={triggerClassName}
            aria-label={ariaLabel}
            data-testid={testId}
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          disableAnimation
          widthMode="trigger"
          style={triggerWidthStyle}
          className={cn("rounded-lg border-0 p-0 shadow-none", menuClassName)}
        >
          <GroupMenu
            groups={groups}
            value={value}
            size={size}
            searchable={searchable}
            searchPlaceholder={searchPlaceholder}
            emptyMessage={emptyMessage}
            onChange={(id) => {
              onChange(id);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </SelectorField>
  );
}
